"use strict";
/**
 * Additional Markets Service
 *
 * Manages "additional market assignments" — extra market orders that fire
 * alongside the primary market when an assigned buy button is pressed
 * (any layout: traditional, baseball, or basketball).
 *
 * Each assignment binds a specific market + side + dollar amount + manual price
 * to one or more buy buttons (e.g. "single", "bk_2ptr", "trad_buy_yes").
 *
 * Assignments are persisted to localStorage with daily cleanup, matching
 * the event-type-service pattern.
 *
 * Button ID types and labels live in the order-button registry
 * (services/order-button-registry.ts) and are re-exported here for
 * back-compat with existing importers.
 *
 * @module services/additional-markets-service
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BUTTON_LABELS = void 0;
exports.getAdditionalAssignments = getAdditionalAssignments;
exports.getAssignmentsForButton = getAssignmentsForButton;
exports.buttonHasAssignments = buttonHasAssignments;
exports.addAssignment = addAssignment;
exports.removeAssignment = removeAssignment;
exports.updateAssignment = updateAssignment;
exports.clearAllAssignments = clearAllAssignments;
exports.removeAssignmentsBySource = removeAssignmentsBySource;
exports.getButtonsWithAssignments = getButtonsWithAssignments;
exports.cleanupStaleAssignments = cleanupStaleAssignments;
const order_button_registry_1 = require("./order-button-registry");
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STORAGE_KEY = "kalshi_additional_markets";
let fallbackIdCounter = 0;
/** Human-readable labels for button IDs (backed by the registry). */
exports.BUTTON_LABELS = order_button_registry_1.ORDER_BUTTON_LABELS;
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getTodayString() {
    return new Date().toISOString().slice(0, 10);
}
function generateId() {
    try {
        if (globalThis.crypto?.randomUUID) {
            return globalThis.crypto.randomUUID();
        }
    }
    catch {
        // Fallback if randomUUID exists but throws (e.g. non-secure context)
    }
    const SUFFIX_LEN = 12;
    const raw = Math.random().toString(36).slice(2);
    fallbackIdCounter += 1;
    return `am-${Date.now()}-${fallbackIdCounter}-${raw.padEnd(SUFFIX_LEN, "0").slice(0, SUFFIX_LEN)}`;
}
// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------
function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return { date: getTodayString(), assignments: [] };
        const parsed = JSON.parse(raw);
        // Daily cleanup: discard stale entries
        if (parsed.date !== getTodayString()) {
            localStorage.removeItem(STORAGE_KEY);
            return { date: getTodayString(), assignments: [] };
        }
        // Validate shape: assignments must be an array
        if (!Array.isArray(parsed.assignments)) {
            localStorage.removeItem(STORAGE_KEY);
            return { date: getTodayString(), assignments: [] };
        }
        return parsed;
    }
    catch {
        return { date: getTodayString(), assignments: [] };
    }
}
function saveToStorage(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Get all current additional market assignments.
 */
function getAdditionalAssignments() {
    return loadFromStorage().assignments;
}
/**
 * Get assignments for a specific button.
 */
function getAssignmentsForButton(buttonId) {
    return getAdditionalAssignments().filter((a) => a.buttons.includes(buttonId));
}
/**
 * Check if a button has any additional market assignments.
 */
function buttonHasAssignments(buttonId) {
    return getAssignmentsForButton(buttonId).length > 0;
}
/**
 * Add a new additional market assignment.
 */
function addAssignment(assignment) {
    const stored = loadFromStorage();
    const newAssignment = {
        ...assignment,
        id: generateId(),
        createdDate: new Date().toISOString(),
    };
    stored.assignments.push(newAssignment);
    saveToStorage(stored);
    return newAssignment;
}
/**
 * Remove a specific assignment by ID.
 */
function removeAssignment(assignmentId) {
    const stored = loadFromStorage();
    stored.assignments = stored.assignments.filter((a) => a.id !== assignmentId);
    saveToStorage(stored);
}
/**
 * Update the configurable fields of an existing assignment.
 * Only side, dollarAmount, manualPrice, and buttons may be changed.
 * If no assignment with the given ID is found, the call is a no-op.
 */
function updateAssignment(assignmentId, updates) {
    const stored = loadFromStorage();
    const assignment = stored.assignments.find((a) => a.id === assignmentId);
    if (!assignment)
        return;
    if (updates.side !== undefined)
        assignment.side = updates.side;
    if (updates.dollarAmount !== undefined)
        assignment.dollarAmount = updates.dollarAmount;
    if (updates.manualPrice !== undefined)
        assignment.manualPrice = updates.manualPrice;
    if (updates.buttons !== undefined)
        assignment.buttons = updates.buttons;
    saveToStorage(stored);
}
/**
 * Clear all additional market assignments.
 */
function clearAllAssignments() {
    localStorage.removeItem(STORAGE_KEY);
}
/**
 * Remove all assignments matching a given source tag.
 */
function removeAssignmentsBySource(source) {
    const stored = loadFromStorage();
    stored.assignments = stored.assignments.filter((a) => a.source !== source);
    saveToStorage(stored);
}
/**
 * Get all unique button IDs that have at least one assignment.
 */
function getButtonsWithAssignments() {
    const assignments = getAdditionalAssignments();
    const result = new Set();
    for (const a of assignments) {
        for (const b of a.buttons) {
            result.add(b);
        }
    }
    return result;
}
/**
 * Clean up stale additional-market data (call on app init).
 */
function cleanupStaleAssignments() {
    loadFromStorage(); // Side-effect: cleans up if date doesn't match today
}
